// RecipientsFieldset — Phase 3 Plan 03-07 Task 07.2 (D-43, scope_split_note).
//
// Standalone controlled component для использования в NewTrip form (P4 DIS-07
// integration). В P3 не wired в NewTrip — tested standalone.
//
// Props (controlled — parent owns value):
//   value: RecipientDraft[]
//   onChange: (next: RecipientDraft[]) => void
//   availableStops?: { id, label }[] — для segment_filter_stop_ids checkbox
//
// Каждая строка: email input + locale select + optional @username + checkboxes
// per stop. Add/Remove кнопки управляют массивом.

import { useTranslation } from "react-i18next";
import type { CustomerLocale } from "../api/customerInvites";
import { TOUCH_TARGET_MIN } from "../lib/tokens";

export interface RecipientDraft {
  recipient_email: string;
  recipient_locale: CustomerLocale;
  recipient_telegram_username?: string | null;
  segment_filter_stop_ids: number[];
}

export interface StopOption {
  id: number;
  label: string;
}

export interface RecipientsFieldsetProps {
  value: RecipientDraft[];
  onChange: (next: RecipientDraft[]) => void;
  availableStops?: StopOption[];
}

const EMPTY_RECIPIENT: RecipientDraft = {
  recipient_email: "",
  recipient_locale: "en",
  recipient_telegram_username: null,
  segment_filter_stop_ids: [],
};

export function RecipientsFieldset({
  value,
  onChange,
  availableStops = [],
}: RecipientsFieldsetProps): React.JSX.Element {
  const { t } = useTranslation();

  function updateAt(index: number, patch: Partial<RecipientDraft>): void {
    const next = value.map((r, i) => (i === index ? { ...r, ...patch } : r));
    onChange(next);
  }

  function addRow(): void {
    onChange([...value, { ...EMPTY_RECIPIENT }]);
  }

  function removeAt(index: number): void {
    onChange(value.filter((_, i) => i !== index));
  }

  function toggleStop(index: number, stopId: number): void {
    const row = value[index];
    if (!row) return;
    const has = row.segment_filter_stop_ids.includes(stopId);
    const segs = has
      ? row.segment_filter_stop_ids.filter((id) => id !== stopId)
      : [...row.segment_filter_stop_ids, stopId];
    updateAt(index, { segment_filter_stop_ids: segs });
  }

  return (
    <fieldset
      data-testid="recipients-fieldset"
      style={{ border: "1px solid #ddd", padding: 12, borderRadius: 8 }}
    >
      <legend>{t("customer_facing:recipients.title", "Recipients")}</legend>
      {value.length === 0 && (
        <p data-testid="recipients-fieldset-empty" style={{ color: "#666" }}>
          {t("customer_facing:recipients.empty", "No recipients yet.")}
        </p>
      )}
      {value.map((row, index) => (
        <div
          key={`recipient-${index}`}
          data-testid={`recipient-row-${index}`}
          style={{ marginBottom: 12, borderBottom: "1px dashed #eee", paddingBottom: 8 }}
        >
          <input
            type="email"
            placeholder={t("customer_facing:recipients.email", "Email") as string}
            value={row.recipient_email}
            onChange={(e) => updateAt(index, { recipient_email: e.target.value })}
            data-testid={`recipient-email-${index}`}
            style={{
              display: "block",
              width: "100%",
              boxSizing: "border-box",
              padding: 8,
              marginBottom: 4,
            }}
          />
          <select
            value={row.recipient_locale}
            onChange={(e) =>
              updateAt(index, { recipient_locale: e.target.value as CustomerLocale })
            }
            data-testid={`recipient-locale-${index}`}
            style={{
              display: "block",
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              padding: 8,
              marginBottom: 4,
            }}
          >
            <option value="en">EN</option>
            <option value="de">DE</option>
            <option value="fr">FR</option>
          </select>
          <input
            type="text"
            placeholder={
              t("customer_facing:recipients.telegram", "Telegram username (optional)") as string
            }
            value={row.recipient_telegram_username ?? ""}
            onChange={(e) =>
              updateAt(index, {
                recipient_telegram_username: e.target.value || null,
              })
            }
            data-testid={`recipient-telegram-${index}`}
            style={{
              display: "block",
              width: "100%",
              boxSizing: "border-box",
              padding: 8,
              marginBottom: 4,
            }}
          />
          {availableStops.length > 0 && (
            <div data-testid={`recipient-stops-${index}`}>
              <p style={{ marginBottom: 4 }}>
                {t("customer_facing:recipients.stop_filter", "Visible stops")}
              </p>
              {availableStops.map((s) => (
                <label key={s.id} style={{ display: "inline-block", marginRight: 8 }}>
                  <input
                    type="checkbox"
                    checked={row.segment_filter_stop_ids.includes(s.id)}
                    onChange={() => toggleStop(index, s.id)}
                    data-testid={`recipient-stop-${index}-${s.id}`}
                  />
                  {s.label}
                </label>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => removeAt(index)}
            data-testid={`recipient-remove-${index}`}
            style={{
              minHeight: TOUCH_TARGET_MIN,
              minWidth: TOUCH_TARGET_MIN,
              marginTop: 4,
            }}
          >
            {t("customer_facing:recipients.remove", "Remove")}
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        data-testid="recipients-add"
        style={{ minHeight: TOUCH_TARGET_MIN, padding: 12 }}
      >
        + {t("customer_facing:recipients.add", "Add recipient")}
      </button>
    </fieldset>
  );
}
